import {
  RefObject,
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import Flatpickr from 'react-flatpickr';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';

import { getCountry } from 'i18n/locale';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { LodgingDataEvent, useAnalytics } from 'util/analytics';

import { Button } from 'components/blocks/Button';
import { SvgCalendar } from 'components/icons';

import classNames from 'classnames';
import { ITALodgingReq } from '../types';
import { getLodgingFromQuery } from '../utils';
import styles from '../styles.module.scss';

interface IFormProps {
  searchRedirectUrl: string,
  onChange?: (state: ITALodgingReq) => void
}

export const Form = ({ onChange, searchRedirectUrl }: IFormProps) => {
  const { t } = useOTSTranslation('common');
  const analytics = useAnalytics();
  const initial = getLodgingFromQuery();
  const router = useRouter();
  const checkInRef = useRef<Flatpickr>(null);
  const checkOutRef = useRef<Flatpickr>(null);

  const openDatePicker = (ref:RefObject<Flatpickr>) => {
    const instance = ref.current?.flatpickr;
    if (instance) {
      instance.open();
    }
  };

  const [checkIn, setCheckIn] = useState<Date>(initial.checkIn.toJSDate());
  const [checkOut, setCheckOut] = useState<Date>(initial.checkOut.toJSDate());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [adultNum, setAdultNum] = useState<number>(initial.numAdults);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [roomNum, setRoomNum] = useState<number>(initial.numRooms);

  const today = useMemo(() => DateTime.now().startOf('day').toJSDate(), []);
  const dayAfterCheckIn = useMemo(() => DateTime.fromJSDate(checkIn).plus({ day: 1 }).toJSDate(), [checkIn]);

  const onClick = useCallback((event: any) => {
    event.preventDefault();
    if (onChange) {
      onChange({
        numAdults: adultNum,
        numRooms: roomNum,
        checkOut: DateTime.fromJSDate(checkOut).toFormat('yyyy-MM-dd'),
        checkIn: DateTime.fromJSDate(checkIn).toFormat('yyyy-MM-dd'),
      });
    }
    const query = {
      from: DateTime.fromJSDate(checkIn).toFormat('yyyy-MM-dd'),
      to: DateTime.fromJSDate(checkOut).toFormat('yyyy-MM-dd'),
      adult: adultNum,
      rms: roomNum,
    };
    if (onChange) {
      router.replace({
        pathname: searchRedirectUrl,
        query,
      }, undefined, {
        scroll: false,
      });
    } else {
      router.push({
        pathname: searchRedirectUrl,
        query,
      }, undefined, {
        scroll: false,
      });
    }
    analytics.logdingEvent(LodgingDataEvent.LodgingSearch, {
      checkOut: DateTime.fromJSDate(checkOut).toFormat('yyyy-MM-dd'),
      checkIn: DateTime.fromJSDate(checkIn).toFormat('yyyy-MM-dd'),
    });
  }, [roomNum, adultNum, checkIn, checkOut, searchRedirectUrl]);

  const handleCheckIn = useCallback((d: Date[]) => {
    setCheckIn(d[0]);

    if (checkOut.getTime() <= d[0].getTime()) {
      setCheckOut(DateTime.fromJSDate(d[0]).plus({ day: 1 }).toJSDate());
    }
  }, [setCheckIn]);

  const handleCheckOut = useCallback(
    (d: Date[]) => { setCheckOut(d[0]); },
    [setCheckOut],
  );

  useEffect(() => {
    if (onChange) {
      onChange({
        numAdults: adultNum,
        numRooms: roomNum,
        checkOut: DateTime.fromJSDate(checkOut).toFormat('yyyy-MM-dd'),
        checkIn: DateTime.fromJSDate(checkIn).toFormat('yyyy-MM-dd'),
      });
    }
  }, [initial]);

  return (
    <form className={styles.wrapper}>
      <div className={styles.form}>
        <div>
          <div className={styles.dateInput}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="fromDate">{t('tripWidget.checkIn')}</label>
            <div className={classNames(styles.flex, 'flatpickr')}>
              <Flatpickr
                ref={checkInRef}
                id="fromDate"
                type="text"
                value={checkIn}
                options={{
                  minDate: today,
                  altFormat: getCountry() === 'US' ? 'm/d/Y' : 'd/m/Y',
                  disableMobile: true,
                  altInput: true,
                }}
                onChange={handleCheckIn}
              />
              <button type="button" onClick={() => openDatePicker(checkInRef)}>
                <SvgCalendar />
              </button>
            </div>
          </div>
          <div className={styles.dateInput}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="toDate">{t('tripWidget.checkOut')}</label>
            <div className={styles.flex}>
              <Flatpickr
                ref={checkOutRef}
                id="toDate"
                options={
              {
                minDate: dayAfterCheckIn,
                altFormat: getCountry() === 'US' ? 'm/d/Y' : 'd/m/Y',
                disableMobile: true,
                altInput: true,
              }
            }
                type="text"
                value={checkOut}
                onChange={handleCheckOut}
              />
              <button type="button" onClick={() => openDatePicker(checkOutRef)}>
                <SvgCalendar />
              </button>
            </div>
          </div>
        </div>

        <div>
          <div className={styles.button}>
            <Button onClick={onClick}>{t('common.searchButton')}</Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default Form;
